/**
 * Cart sync orchestrator.
 *
 * Ties the pure pieces (canonical form, decision procedure) to the impure ones
 * (transport, localStorage, the Shopify cart) and enforces the ordering rules
 * that keep a shared cart safe. See docs/CART_SYNC_PLAN.md §6.
 *
 * Every failure path is silent to the customer: if anything goes wrong the cart
 * simply behaves as it does today, device-local.
 */

import type { CartDiffResult } from "../../types/cart.js";
import { fetchCart, groupCartLines } from "../cartService.js";
import { showToast } from "../../components/toast.js";
import { applyTarget, blankCheckoutAttributes } from "./apply.js";
import type { CanonicalCart } from "./canonical.js";
import {
  bareCartToken,
  canonicalFromGroups,
  hasPurchasableLines,
  canonicalFromServer,
  canonicalToItems,
  buildLineOps,
  chunkOps,
  displayMapFromCart,
  LIMITS,
  hashCanonical,
  needsLineRepair,
} from "./canonical.js";
import { decide, EMPTY_LOCAL_SETTLE_MS, SyncAction } from "./decision.js";
import { runWithSyncLock, tryBecomePollLeader, withSyncLock } from "./lock.js";
import {
  clearState,
  getDeviceId,
  loadGuards,
  loadState,
  saveState,
  setApplyInProgress,
  setClearEvidence,
  setEmptyLocalSince,
} from "./state.js";
import { beaconPush, fetchHistoryVersion, pullCart, pushOps } from "./transport.js";
import type { CartDisplay, CartOp, PublicCartState, SyncState } from "./types.js";
import { CartOpType, ClearedReason, SyncFailure } from "./types.js";

const FAST_POLL_MS = 30_000;
const SLOW_POLL_MS = 120_000;
/** Treat a coworker as "active" for this long after their last write. */
const OTHER_DEVICE_ACTIVE_WINDOW_MS = 10 * 60 * 1000;
const PUSH_DEBOUNCE_MS = 2000;
const MAX_APPLY_ATTEMPTS = 3;

interface Identity {
  readonly customerKey: string;
  readonly locationId: number;
}

let identity: Identity | null = null;
/** Set on 403: this page must stop syncing entirely rather than keep guessing. */
let disabledForPage = false;
let pollTimer: number | null = null;
let pushTimer: number | null = null;
let pendingCycle = false;
/** Keys with an unsent or in-flight local intent — an apply must not delete them. */
const pendingKeys = new Set<string>();
/** Debounced grid ops, last-op-wins per line, matching the server's coalescing. */
const queuedOps = new Map<string, CartOp>();
/**
 * Registered by the product page so an apply can refresh the grid.
 * Receives the keys the apply actually changed — the page must re-baseline
 * exactly those, or every applied row stays permanently dirty.
 */
let afterApplyHook:
  | ((appliedKeys: ReadonlySet<string>) => void | Promise<void>)
  | null = null;

function log(message: string, detail?: unknown): void {
  // Breadcrumbs only. A sync problem must never surface as a customer error.
  console.debug(`[cart-sync] ${message}`, detail ?? "");
}

function newBatchId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function isSyncEnabled(): boolean {
  return identity !== null && !disabledForPage;
}

export function registerAfterApplyHook(
  hook: (appliedKeys: ReadonlySet<string>) => void | Promise<void>,
): void {
  afterApplyHook = hook;
}

/** Local cart in canonical form, plus the raw cart for token/display capture. */
async function readLocal(): Promise<{
  canonical: CanonicalCart;
  cartToken: string | null;
  displays: Map<string, CartDisplay>;
  duplicated: boolean;
} | null> {
  try {
    const cart = await fetchCart();
    const groups = groupCartLines(cart);
    return {
      canonical: canonicalFromGroups(groups),
      // Registered ONLY for a cart that holds something purchasable.
      //
      // cart.CartTokens keeps one row per device and every pull overwrites it,
      // but the orders/create webhook looks up the token that was CHECKED OUT.
      // Shopify hands the browser a fresh empty cart the moment an order
      // completes, and `initCartSync` pulls the instant the buyer lands back on
      // the storefront — so registering that empty cart's token erased the only
      // token that could still match, and the tombstone was lost. One customer
      // placed three orders in three minutes that way on 2026-08-20.
      //
      // An empty cart is never the cart an order came from, so its token is
      // worth nothing and costs the one that matters. Fees do not count as
      // contents: nobody checks out a service charge on its own.
      cartToken: hasPurchasableLines(cart) ? bareCartToken(cart.token) : null,
      displays: displayMapFromCart(cart),
      duplicated: needsLineRepair(groups).length > 0,
    };
  } catch (error: unknown) {
    log("local cart unavailable", error);
    return null;
  }
}

function persist(partial: Partial<SyncState> & { lastAppliedVersion: number; lastAppliedHash: string }): void {
  if (!identity) return;
  const previous = loadState();
  saveState({
    customerKey: identity.customerKey,
    locationId: identity.locationId,
    lastAppliedVersion: partial.lastAppliedVersion,
    lastAppliedHash: partial.lastAppliedHash,
    lastPushedCartToken:
      partial.lastPushedCartToken ?? previous?.lastPushedCartToken ?? null,
    unavailableLines: partial.unavailableLines ?? previous?.unavailableLines ?? [],
  });
}

/**
 * Send one batch and return the resulting server state.
 *
 * Guarded ops NEVER auto-rebase on 409: rebasing a clear/replace/restore onto a
 * newer version destroys exactly the concurrent write the version guard exists
 * to protect. The caller adopts the server's current state instead.
 */
async function sendBatch(
  ops: readonly CartOp[],
  baseVersion: number,
  cartToken: string | null,
): Promise<PublicCartState | null> {
  if (!identity) return null;
  const result = await pushOps({
    locationId: identity.locationId,
    baseVersion,
    opBatchId: newBatchId(),
    deviceId: getDeviceId(),
    ...(cartToken ? { cartToken } : {}),
    ops,
  });

  if (result.ok) return result.value;

  if (result.failure === SyncFailure.Forbidden) {
    disabledForPage = true;
    log("sync disabled for this page", result.detail);
    return null;
  }
  if (result.failure === SyncFailure.BadRequest) {
    // Our payload was wrong. Never evidence about cart state.
    log("push rejected as invalid", result.detail);
    return null;
  }
  if (result.failure === SyncFailure.Conflict) {
    log("version conflict — adopting server state", result.detail);
    return result.current ?? null;
  }
  log("push unavailable", result.detail);
  return null;
}

/**
 * Push line ops in server-sized chunks, rebasing from each response.
 *
 * Reports whether EVERY chunk landed. A partial push that is recorded as
 * "in sync" is silent, permanent data loss: the un-pushed lines never reach the
 * server, the local hash says there is nothing to reconcile, and the next
 * device to push causes them to be deleted locally too.
 */
async function pushLineOps(
  ops: readonly CartOp[],
  startVersion: number,
  cartToken: string | null,
): Promise<{ state: PublicCartState | null; complete: boolean }> {
  let version = startVersion;
  let latest: PublicCartState | null = null;
  for (const batch of chunkOps(ops)) {
    const state = await sendBatch(batch, version, cartToken);
    if (!state) return { state: latest, complete: false };
    latest = state;
    version = state.version;
  }
  return { state: latest, complete: true };
}

/**
 * Refuse to sync a cart the server will reject outright.
 *
 * Beyond the cap the server 400s every batch, and a client that keeps pushing
 * would chunk its way into a partial write. A visible hard stop is the
 * documented behavior; a silent partial sync is not.
 */
function exceedsCartCap(canonical: CanonicalCart): boolean {
  return canonical.size > LIMITS.maxItemsPerCart;
}

function warnCartTooLarge(size: number): void {
  showToast(
    `This cart has ${size} lines, more than cross-device sync supports (${LIMITS.maxItemsPerCart}). Your cart still works normally on this device.`,
    { key: "cart-sync-too-large", durationMs: 0 },
  );
}

/** Record the outcome of a successful sync against the actual local cart. */
async function commit(server: PublicCartState, cartToken: string | null): Promise<void> {
  const local = await readLocal();
  if (!local) return;
  persist({
    lastAppliedVersion: server.version,
    // Always from a fresh readback — never the response body — so stock clamps
    // and dropped lines are absorbed instead of looking like local edits.
    lastAppliedHash: hashCanonical(local.canonical),
    lastPushedCartToken: cartToken ?? local.cartToken,
  });
}

/**
 * Save the local lines into server history before something destroys them.
 *
 * Returns false if the stash did NOT land. The caller must then abandon the
 * destructive apply: proceeding anyway is the one case the stash exists to
 * prevent — "your cart changed" silently becoming "we deleted your work".
 */
async function stashLocal(
  local: CanonicalCart,
  displays: Map<string, CartDisplay>,
  baseVersion: number,
): Promise<boolean> {
  const items = canonicalToItems(local, (key) => displays.get(key));
  if (items.length === 0) return true;
  if (items.length > LIMITS.maxItemsPerCart) {
    // stashHistory must be solo, so it cannot be chunked.
    log("cart too large to stash; refusing to apply over it", items.length);
    return false;
  }
  const state = await sendBatch(
    [{ op: CartOpType.StashHistory, items }],
    baseVersion,
    null,
  );
  return state !== null;
}

async function runApply(
  server: PublicCartState,
  options: { stashFirst: boolean },
): Promise<void> {
  if (!identity) return;
  const local = await readLocal();
  if (!local) return;

  if (options.stashFirst) {
    const stashed = await stashLocal(
      local.canonical,
      local.displays,
      server.version,
    );
    if (!stashed) {
      log("stash failed; skipping this apply so local work is not destroyed");
      return;
    }
  }

  const target = canonicalFromServer(server.items);
  const state = loadState();
  const skip = new Set(state?.unavailableLines ?? []);
  pendingKeys.forEach((key) => skip.add(key));

  const { getDirtyKeys } = await import("../../components/dirtyTracking.js");
  const held = getDirtyKeys();

  // Preserve the attempt counter the caller set. Resetting it to 1 here made
  // the give-up check in runCycle unreachable, so a persistently failing apply
  // wedged every future cycle into ResumeApply forever.
  const priorAttempts = loadGuards().applyInProgress?.attempts ?? 1;
  setApplyInProgress({
    targetVersion: server.version,
    targetItems: canonicalToItems(target),
    attempts: priorAttempts,
  });

  let outcome;
  try {
    outcome = await applyTarget(target, { heldKeys: held, skipKeys: skip });
  } catch (error: unknown) {
    // Leave applyInProgress set so the next cycle resumes — but the attempt
    // counter now really advances, so a permanently failing apply gives up
    // instead of wedging sync forever.
    log("apply failed; will retry on the next cycle", error);
    return;
  }
  setApplyInProgress(null);

  const failed = [...(state?.unavailableLines ?? []), ...outcome.failedKeys];
  persist({
    // A partial apply must NOT claim to be fully synced: recording the target
    // version while differing on the held lines makes every later cycle
    // short-circuit and loses a coworker's edit permanently and invisibly.
    lastAppliedVersion:
      outcome.heldKeys.length > 0 ? (state?.lastAppliedVersion ?? 0) : server.version,
    lastAppliedHash: hashCanonical(outcome.canonical),
    unavailableLines: Array.from(new Set(failed)),
  });

  if (outcome.failedKeys.length > 0) {
    showToast(
      `${outcome.failedKeys.length} item${outcome.failedKeys.length === 1 ? "" : "s"} from your saved cart ${outcome.failedKeys.length === 1 ? "is" : "are"} no longer available.`,
      { key: "cart-sync-unavailable" },
    );
  }
  // These messages used to assert "another device" unconditionally, which is a
  // claim the client cannot make: the device id is localStorage-scoped, so one
  // browser's two TABS are one device and can never be told apart. The result
  // was that a shopper's own purchase was reported to them as another device
  // clearing their cart, in a banner that never dismissed — and it was read as
  // the cart-sync bug recurring. Say what is known; attribute nothing.
  if (outcome.heldKeys.length > 0) {
    showToast("Cart updated. Your unsaved rows were kept.", { key: "cart-sync-held" });
  } else if (server.cleared) {
    showToast(clearedMessage(server.clearedReason), { key: "cart-sync-cleared" });
  } else {
    showToast("Cart updated with your saved changes.", { key: "cart-sync-applied" });
  }

  await afterApplyHook?.(new Set(outcome.appliedKeys));

  // For listeners that must not depend on the single hook slot above, or on it
  // being registered yet (the promotion watcher re-judges the cart on this).
  if (typeof document !== "undefined") document.dispatchEvent(new CustomEvent("cart-sync:applied"));
}

/**
 * Why the cart just emptied, in the shopper's terms.
 *
 * `purchase` is the common case by a wide margin and the one that most needs
 * saying plainly — it is the reassurance that the order went through, which is
 * exactly what a shopper doubts when their cart empties.
 */
function clearedMessage(reason: ClearedReason | null): string {
  switch (reason) {
    case ClearedReason.Purchase:
      return "Your order was placed — this cart has been cleared.";
    case ClearedReason.Expired:
      return "This saved cart expired and was cleared.";
    default:
      return "This cart was cleared.";
  }
}

/** One full sync cycle. Safe to call as often as you like; it self-serializes. */
export async function runCycle(): Promise<void> {
  if (!isSyncEnabled() || !identity) return;

  const outcome = await withSyncLock(async () => {
    const local = await readLocal();
    const deviceId = getDeviceId();
    const pull = await pullCart({
      locationId: identity!.locationId,
      deviceId,
      cartToken: local?.cartToken ?? null,
    });

    if (!pull.ok) {
      if (pull.failure === SyncFailure.Forbidden) {
        disabledForPage = true;
        log("sync disabled for this page", pull.detail);
      }
      return;
    }

    const server = pull.value;
    const state = loadState();
    const guards = loadGuards();

    const decision = decide({
      identity: identity!,
      state,
      guards,
      local: local?.canonical ?? null,
      server,
      now: Date.now(),
    });

    log(`decision: ${decision.action} (${decision.reason})`);

    // The settle timer describes ONE unbroken run of unexplained emptiness. Any
    // other verdict ends that run, so a stamp left behind by an earlier one must
    // not shorten the next wait — that would hand back the race it prevents.
    if (decision.action !== SyncAction.WaitForSettle && guards.emptyLocalSince !== null) {
      setEmptyLocalSince(null);
    }

    switch (decision.action) {
      case SyncAction.Abort:
      case SyncAction.Idle:
        return;

      case SyncAction.WaitForSettle: {
        // Start the clock on first sight, then look again once it has run out.
        // The re-check is scheduled rather than left to the poll because the
        // poll is 30-120 s away and the buyer is staring at the cart now.
        if (guards.emptyLocalSince === null) {
          setEmptyLocalSince(Date.now());
          scheduleCycle(EMPTY_LOCAL_SETTLE_MS);
          return;
        }
        const remaining = EMPTY_LOCAL_SETTLE_MS - (Date.now() - guards.emptyLocalSince);
        scheduleCycle(remaining > 0 ? remaining : 0);
        return;
      }

      case SyncAction.ResumeApply: {
        const apply = guards.applyInProgress;
        if (!apply) return;
        if (apply.attempts >= MAX_APPLY_ATTEMPTS) {
          // A wedged flag blocks every push forever, which is worse than being
          // unsynced. Give up, keep the breadcrumb, resume normal operation.
          setApplyInProgress(null);
          log("abandoning stuck apply after repeated failures");
          return;
        }
        setApplyInProgress({ ...apply, attempts: apply.attempts + 1 });
        // Recompute against the CURRENT server state; a stale target is worse
        // than no target.
        await runApply(server, { stashFirst: false });
        return;
      }

      case SyncAction.ClearLocalCart: {
        clearState();
        try {
          await fetch("/cart/clear.js", { method: "POST" });
        } catch {
          /* best effort */
        }
        return;
      }

      case SyncAction.ResetState: {
        clearState();
        return;
      }

      case SyncAction.PushFirstContact:
      case SyncAction.PushDrift: {
        if (!local) return;
        if (exceedsCartCap(local.canonical)) {
          warnCartTooLarge(local.canonical.size);
          return;
        }
        // Diff against what the server actually holds right now. Both branches
        // that land here — first contact and drift — want the same thing: ops
        // that move the server's current state to this device's local state.
        const currentServer = canonicalFromServer(server.items);
        const ops = buildLineOps(currentServer, local.canonical, (key) =>
          local.displays.get(key),
        );
        if (ops.length === 0) {
          await commit(server, local.cartToken);
          return;
        }
        const { state: pushed, complete } = await pushLineOps(
          ops,
          server.version,
          local.cartToken,
        );
        // Only claim to be in sync when EVERY chunk landed. Committing a hash
        // for lines the server never received loses them permanently: the next
        // cycle sees no drift, and the next device to push deletes them here.
        if (pushed && complete) await commit(pushed, local.cartToken);
        else if (!complete) log("partial push — leaving drift for the next cycle");
        return;
      }

      case SyncAction.ApplyServer:
        await runApply(server, { stashFirst: decision.stashLocalFirst === true });
        return;

      default:
        return;
    }
  });

  if (outcome === null) {
    log("cycle skipped (lock contended)");
  }
}

/** Schedule a cycle, coalescing bursts. */
export function scheduleCycle(delayMs = 0): void {
  if (!isSyncEnabled()) return;
  if (pendingCycle) return;
  pendingCycle = true;
  window.setTimeout(() => {
    pendingCycle = false;
    void runCycle();
  }, delayMs);
}

/** A cart diff as sync ops, keyed `variantId|warehouseCode` — a delete is Remove, anything else Set. */
function opsFromDiff(diff: CartDiffResult): Array<[string, CartOp]> {
  const ops: Array<[string, CartOp]> = [];
  diff.toDelete.forEach((op) => {
    ops.push([`${op.variantId}|${op.warehouseCode}`, {
      op: CartOpType.Remove,
      variantId: Number(op.variantId),
      warehouseCode: op.warehouseCode,
    }]);
  });
  const set = (variantId: string, warehouseCode: string, quantity: number): void => {
    ops.push([`${variantId}|${warehouseCode}`, {
      op: CartOpType.Set,
      variantId: Number(variantId),
      warehouseCode,
      quantity,
    }]);
  };
  diff.toUpdate.forEach((op) => set(op.variantId, op.warehouseCode, op.newQty));
  diff.toAdd.forEach((op) => set(op.variantId, op.warehouseCode, op.quantity));
  return ops;
}

/**
 * Push line ops and commit on success — the body both push paths share. The
 * caller holds the sync lock. Returns true only when every chunk landed.
 */
async function pushOpsUnderLock(batch: readonly CartOp[]): Promise<boolean> {
  const state = loadState();
  const local = await readLocal();

  // Re-attach display metadata. `set` replaces the whole line server-side,
  // so pushing without it DELETES the stored title/sku/price — which nulls
  // displayTotalCents for the entire cart and blanks the dollar figure in
  // Scanner's Carts tab and the call-pop line.
  const withDisplay = batch.map((op) => {
    if (op.op !== CartOpType.Set) return op;
    const display = local?.displays.get(
      `${op.variantId}|${op.warehouseCode}`,
    );
    return display ? { ...op, display } : op;
  });

  const { state: pushed, complete } = await pushLineOps(
    withDisplay,
    state?.lastAppliedVersion ?? 0,
    local?.cartToken ?? null,
  );
  if (pushed && complete) await commit(pushed, local?.cartToken ?? null);
  return pushed !== null && complete;
}

/**
 * Mirror a change the page just made to Shopify's cart NOW, and wait for it.
 *
 * For a caller that RELOADS right after (the promotion panel and watcher): the
 * debounced `pushGridDiff` dies with the page, and a change left for "drift" can
 * be undone by the next pull — an emptied cart is read as state loss and
 * restored. Resolves true when it landed, or sync is off; false when the lock
 * was busy or a chunk failed (drift then catches it, as for the page's remove).
 */
export async function pushDiffNow(diff: CartDiffResult): Promise<boolean> {
  if (!isSyncEnabled()) return true;
  const batch = opsFromDiff(diff).map(([, op]) => op);
  if (batch.length === 0) return true;
  const outcome = await runWithSyncLock(() => pushOpsUnderLock(batch));
  return outcome.ran && outcome.value;
}

/**
 * Mirror a grid submit to the server.
 *
 * Takes the diff the grid already computed rather than re-deriving it, which
 * would double-fetch and could miss a line the server clamped in between.
 */
export function pushGridDiff(diff: CartDiffResult): void {
  if (!isSyncEnabled()) return;

  // Accumulate into a shared map rather than capturing this call's ops in the
  // debounce closure. Restarting the timer with a fresh closure DROPPED the
  // previous submit's ops entirely — two quick clicks on adjacent Update Cart
  // buttons lost the first one, and the follow-up commit then recorded the
  // client as fully in sync, so nothing ever repaired it.
  for (const [key, op] of opsFromDiff(diff)) {
    queuedOps.set(key, op);
    pendingKeys.add(key);
  }

  if (queuedOps.size === 0) return;

  if (pushTimer !== null) window.clearTimeout(pushTimer);
  pushTimer = window.setTimeout(() => {
    pushTimer = null;
    const batch = Array.from(queuedOps.values());
    const batchKeys = Array.from(queuedOps.keys());
    queuedOps.clear();

    void withSyncLock(async () => {
      try {
        await pushOpsUnderLock(batch);
      } finally {
        // Always release, or these keys are excluded from every future apply
        // for the life of the page.
        batchKeys.forEach((key) => pendingKeys.delete(key));
      }
    });
  }, PUSH_DEBOUNCE_MS);
}

/**
 * Clear the cart, SERVER FIRST.
 *
 * The order is the whole point: emptying Shopify before the server accepts the
 * clear leaves local-empty/server-full on any failure, which resolves as either
 * the cleared cart coming back or a silent wipe of a coworker's cart.
 *
 * Returns true when the local cart may now be emptied.
 */
export async function requestClear(): Promise<
  { readonly ok: true } | { readonly ok: false; readonly reason: string }
> {
  if (!isSyncEnabled()) return { ok: true }; // sync off: local clear is all there is

  const outcome = await runWithSyncLock(async () => {
    const state = loadState();
    const local = await readLocal();
    const baseVersion = state?.lastAppliedVersion ?? 0;

    const pushed = await pushOps({
      locationId: identity!.locationId,
      baseVersion,
      opBatchId: newBatchId(),
      deviceId: getDeviceId(),
      ...(local?.cartToken ? { cartToken: local.cartToken } : {}),
      ops: [{ op: CartOpType.Clear, reason: ClearedReason.User }],
    });

    if (pushed.ok) {
      setClearEvidence({
        at: Date.now(),
        reason: ClearedReason.User,
        baseVersion,
      });
      persist({
        lastAppliedVersion: pushed.value.version,
        lastAppliedHash: "",
        lastPushedCartToken: local?.cartToken ?? null,
      });
      return { ok: true as const };
    }

    if (pushed.failure === SyncFailure.Conflict) {
      return {
        ok: false as const,
        reason:
          "Someone else changed this cart while you were looking at it. It has been refreshed — please review before clearing.",
      };
    }
    // Server unreachable: allow the local clear (today's behavior) and leave
    // evidence so the empty cart is not mistaken for state loss.
    setClearEvidence({ at: Date.now(), reason: ClearedReason.User, baseVersion });
    return { ok: true as const };
  });

  if (outcome.ran) return outcome.value;

  // The critical section never ran, so the server has NO tombstone and there is
  // no clear evidence. Reporting success here would empty the local cart and
  // then have the next pull put it straight back — the exact bug this feature
  // exists to remove. Make the user retry instead.
  return {
    ok: false,
    reason: "Cart sync is busy — please try clearing again in a moment.",
  };
}

/** Best-effort tombstone flush when the page is going away mid-clear. */
export function beaconClear(baseVersion: number): boolean {
  if (!isSyncEnabled() || !identity) return false;
  return beaconPush({
    locationId: identity.locationId,
    baseVersion,
    opBatchId: newBatchId(),
    deviceId: getDeviceId(),
    ops: [{ op: CartOpType.Clear, reason: ClearedReason.User }],
  });
}

export interface HistoryEntry {
  readonly version: number;
  readonly updatedAt: string;
  readonly itemCount: number;
  readonly totalQuantity: number;
  readonly cleared: boolean;
}

/**
 * History for the restore dropdown.
 *
 * Drops the newest entry when it duplicates the live version — the server writes
 * a history row for the version it just wrote, so the live cart always appears
 * in its own "restore an earlier cart" list.
 */
export async function fetchHistory(): Promise<HistoryEntry[]> {
  if (!isSyncEnabled() || !identity) return [];
  const local = await readLocal();
  const pull = await pullCart({
    locationId: identity.locationId,
    deviceId: getDeviceId(),
    cartToken: local?.cartToken ?? null,
  });
  if (!pull.ok) return [];
  return pull.value.history.filter((entry) => entry.version !== pull.value.version);
}

/** Restore a historical version. The server swaps content atomically. */
export async function restoreVersion(
  version: number,
): Promise<{ readonly ok: boolean; readonly reason?: string }> {
  if (!isSyncEnabled() || !identity) return { ok: false, reason: "Sync unavailable." };

  const result = await withSyncLock(async () => {
    const state = loadState();
    const local = await readLocal();
    const pushed = await pushOps({
      locationId: identity!.locationId,
      baseVersion: state?.lastAppliedVersion ?? 0,
      opBatchId: newBatchId(),
      deviceId: getDeviceId(),
      ...(local?.cartToken ? { cartToken: local.cartToken } : {}),
      ops: [{ op: CartOpType.Restore, historyVersion: version }],
    });

    if (pushed.ok) {
      await runApply(pushed.value, { stashFirst: false });
      await blankCheckoutAttributes();
      return { ok: true };
    }
    if (pushed.failure === SyncFailure.NotFound) {
      return { ok: false, reason: "That cart is no longer available to restore." };
    }
    if (pushed.failure === SyncFailure.Conflict) {
      return { ok: false, reason: "This cart just changed. Please try again." };
    }
    return { ok: false, reason: "Could not reach the cart service." };
  });

  return result ?? { ok: false, reason: "Busy — please try again." };
}

/** Snapshot a historical version's contents (for a future preview UI). */
export async function fetchVersionContents(
  version: number,
): Promise<PublicCartState | null> {
  if (!isSyncEnabled() || !identity) return null;
  const result = await fetchHistoryVersion(identity.locationId, version);
  return result.ok ? result.value : null;
}

function schedulePoll(nextMs: number): void {
  if (pollTimer !== null) window.clearTimeout(pollTimer);
  pollTimer = window.setTimeout(() => {
    pollTimer = null;
    if (document.visibilityState === "visible") {
      void runCycle().finally(() => schedulePoll(nextMs));
    } else {
      schedulePoll(SLOW_POLL_MS);
    }
  }, nextMs);
}

/**
 * Start cart sync.
 *
 * `collaborative` enables the polling loop; it is on for the product and cart
 * pages, where two employees sharing a login actually work side by side, and off
 * elsewhere (those pages only need to pull once so the header badge is right).
 */
export function initCartSync(options: {
  customerKey: string;
  locationId: number;
  collaborative: boolean;
}): void {
  if (identity) return;
  identity = {
    customerKey: options.customerKey,
    locationId: options.locationId,
  };

  void runCycle();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") scheduleCycle(0);
  });
  window.addEventListener("focus", () => scheduleCycle(0));

  // A purchase empties the cart on a page we do not run on, so re-sync as soon
  // as the buyer lands back on the storefront.
  if (document.referrer.includes("/checkout") || document.referrer.includes("thank")) {
    scheduleCycle(0);
  }

  if (!options.collaborative) return;

  void tryBecomePollLeader().then((isLeader) => {
    if (!isLeader) return;
    schedulePoll(FAST_POLL_MS);
  });
}

/** Exposed for the cart page's inline script, which is not part of the bundle. */
export function getPollIntervalForActivity(otherDeviceActiveAt: string | null): number {
  if (!otherDeviceActiveAt) return SLOW_POLL_MS;
  const age = Date.now() - Date.parse(otherDeviceActiveAt);
  return Number.isFinite(age) && age < OTHER_DEVICE_ACTIVE_WINDOW_MS
    ? FAST_POLL_MS
    : SLOW_POLL_MS;
}
